import React, { useState } from 'react';
import styled from 'styled-components';

interface PrivateKeyModalProps {
  isOpen: boolean;
  privateKey: string;
  walletAddress: string;
  tokenAddress: string;
  onClose: () => void;
}

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(8px);
  z-index: 10000;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const ModalContainer = styled.div`
  background: #1a1a1a;
  border: 2px solid #9535fc;
  border-radius: 12px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(149, 53, 252, 0.3);
  
  @media (max-width: 700px) {
    max-width: 95%;
    max-height: 95vh;
  }
`;

const ModalHeader = styled.div`
  background: linear-gradient(135deg, #9535fc 0%, #7c2dd4 100%);
  padding: 24px;
  border-radius: 10px 10px 0 0;
  text-align: center;
`;

const ModalTitle = styled.h2`
  color: #fff;
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 8px 0;
`;

const ModalSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  margin: 0;
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const WarningBox = styled.div`
  background: rgba(255, 69, 58, 0.1);
  border: 2px solid #ff453a;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
`;

const WarningTitle = styled.div`
  color: #ff453a;
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const WarningText = styled.p`
  color: #fff;
  font-size: 14px;
  line-height: 1.6;
  margin: 0 0 8px 0;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  strong {
    color: #ff453a;
    font-weight: 700;
  }
`;

const InfoBox = styled.div`
  background: rgba(149, 53, 252, 0.1);
  border: 1px solid rgba(149, 53, 252, 0.3);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
`;

const InfoTitle = styled.div`
  color: #9535fc;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 8px;
`;

const InfoText = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 13px;
  line-height: 1.5;
  margin: 0 0 4px 0;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const PrivateKeyBox = styled.div`
  background: #252525;
  border: 2px solid #ff453a;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  position: relative;
`;

const PrivateKeyLabel = styled.div`
  color: #ff453a;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PrivateKeyValue = styled.div`
  color: #fff;
  font-size: 14px;
  font-family: 'Courier New', monospace;
  word-break: break-all;
  line-height: 1.6;
  padding: 12px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 6px;
  margin-bottom: 12px;
`;

const CopyButton = styled.button`
  background: #9535fc;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
  
  &:hover {
    background: #7c2dd4;
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const InstructionsBox = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
`;

const InstructionsTitle = styled.div`
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 12px;
`;

const InstructionStep = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 13px;
  line-height: 1.6;
  margin-bottom: 8px;
  padding-left: 20px;
  position: relative;
  
  &:before {
    content: attr(data-step);
    position: absolute;
    left: 0;
    color: #9535fc;
    font-weight: 700;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const CheckboxContainer = styled.div`
  margin-bottom: 24px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  color: #fff;
  font-size: 14px;
  line-height: 1.6;
  
  input[type="checkbox"] {
    margin-top: 4px;
    width: 20px;
    height: 20px;
    cursor: pointer;
    accent-color: #9535fc;
  }
`;

const CloseButton = styled.button<{ disabled: boolean }>`
  background: ${props => props.disabled ? '#444' : '#9535fc'};
  color: ${props => props.disabled ? '#888' : '#fff'};
  border: none;
  border-radius: 8px;
  padding: 14px 24px;
  font-size: 16px;
  font-weight: 700;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;
  width: 100%;
  opacity: ${props => props.disabled ? 0.5 : 1};
  
  &:hover {
    background: ${props => props.disabled ? '#444' : '#7c2dd4'};
    transform: ${props => props.disabled ? 'none' : 'translateY(-2px)'};
  }
  
  &:active {
    transform: ${props => props.disabled ? 'none' : 'translateY(0)'};
  }
`;

const PrivateKeyModal: React.FC<PrivateKeyModalProps> = ({
  isOpen,
  privateKey,
  walletAddress,
  tokenAddress,
  onClose,
}) => {
  const [hasAcknowledged, setHasAcknowledged] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(privateKey);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleClose = () => {
    if (hasAcknowledged) {
      onClose();
      // Reset state for next time
      setHasAcknowledged(false);
    }
  };

  // Prevent closing by clicking overlay if not acknowledged
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && hasAcknowledged) {
      handleClose();
    }
  };

  return (
    <ModalOverlay isOpen={isOpen} onClick={handleOverlayClick}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>🎉 Token Deployed Successfully!</ModalTitle>
          <ModalSubtitle>Your Creator Wallet Has Been Generated</ModalSubtitle>
        </ModalHeader>

        <ModalBody>
          {/* Success Info */}
          <InfoBox>
            <InfoTitle>✅ Deployment Complete</InfoTitle>
            <InfoText>Token Address: {tokenAddress}</InfoText>
            <InfoText>Your Creator Wallet: {walletAddress}</InfoText>
          </InfoBox>

          {/* Critical Warning */}
          <WarningBox>
            <WarningTitle>
              <span>⚠️</span>
              <span>CRITICAL - READ CAREFULLY</span>
            </WarningTitle>
            <WarningText>
              <strong>COPY AND SAVE THIS PRIVATE KEY TO IMPORT YOUR CREATOR WALLET.</strong>
            </WarningText>
            <WarningText>
              <strong>YOU WILL NOT BE ABLE TO EARN CREATOR REWARDS IF YOU DO NOT.</strong>
            </WarningText>
            <WarningText>
              <strong>ANY FUTURE POTENTIAL EARNINGS WILL BE LOST.</strong>
            </WarningText>
            <WarningText style={{ marginTop: '12px', fontSize: '13px' }}>
              This is the ONLY time you will see this key. We do NOT store it anywhere.
            </WarningText>
          </WarningBox>

          {/* Private Key Display */}
          <PrivateKeyBox>
            <PrivateKeyLabel>
              <span>🔑</span>
              <span>YOUR PRIVATE KEY (SAVE THIS NOW!)</span>
            </PrivateKeyLabel>
            <PrivateKeyValue>{privateKey}</PrivateKeyValue>
            <CopyButton onClick={handleCopy}>
              {copySuccess ? '✅ Copied!' : '📋 Copy Private Key'}
            </CopyButton>
          </PrivateKeyBox>

          {/* Instructions */}
          <InstructionsBox>
            <InstructionsTitle>📱 How to Import Your Wallet (MetaMask)</InstructionsTitle>
            <InstructionStep data-step="1.">Copy your private key above</InstructionStep>
            <InstructionStep data-step="2.">Open MetaMask browser extension</InstructionStep>
            <InstructionStep data-step="3.">Click the account icon → "Import Account"</InstructionStep>
            <InstructionStep data-step="4.">Select "Private Key" and paste your key</InstructionStep>
            <InstructionStep data-step="5.">Click "Import" - You can now claim creator fees!</InstructionStep>
          </InstructionsBox>

          {/* Acknowledgment Checkbox */}
          <CheckboxContainer>
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={hasAcknowledged}
                onChange={(e) => setHasAcknowledged(e.target.checked)}
              />
              <span>
                I understand that I must save my private key now. If I lose it, I will lose access to all creator fees forever. 
                I acknowledge that this platform does NOT store my private key and cannot recover it for me.
              </span>
            </CheckboxLabel>
          </CheckboxContainer>

          {/* Close Button */}
          <CloseButton
            disabled={!hasAcknowledged}
            onClick={handleClose}
          >
            {hasAcknowledged ? 'I Have Saved My Key - Close' : 'Please Acknowledge Above to Continue'}
          </CloseButton>
        </ModalBody>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default PrivateKeyModal;

